'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useAssistantAction } from '@/contexts/AssistantActionContext';
import { useProfile } from '@/contexts/ProfileContext';

import { toast } from 'react-hot-toast';
import { centerCrop, makeAspectCrop, Crop, PixelCrop } from 'react-image-crop';
import { formatAvatarUrl } from '@/utils/avatarUtils';
import { apiClient } from '@/app/api/client';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/common/Tabs';
import { PlusIcon, CheckCircleIcon } from 'lucide-react';

import {
  ProfileSection,
  PersonalInfoSection,
  TimezoneSection,
  ImageCropModal,
  OrganizationProfileSection,
  GeoAuthoritySection,
  PreferencesSection,
  BusinessLogoSection,
  AIShortcutsSection,
  PersonaManagementTab,
} from './components';
import ProfileManagerModal from '@/components/profile/ProfileManagerModal';

// This function centers and creates an aspect crop
function centerAspectCrop(
  mediaWidth: number,
  mediaHeight: number,
  aspect: number,
) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight,
    ),
    mediaWidth,
    mediaHeight,
  );
}

export default function ProfilePage() {
  const { user, refreshUserStatus } = useAuth();
  const { triggerAssistant } = useAssistantAction();
  const { profiles, refreshProfiles, createProfile } = useProfile();

  const [activeTab, setActiveTab] = useState<string>('account');
  const [profileData, setProfileData] = useState<any>(null);
  const [isManagerModalOpen, setIsManagerModalOpen] = useState(false);

  // Basic profile state (Root User)
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  // Avatar state
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [avatarLoading, setAvatarLoading] = useState(false);

  // Global GEO Authority state
  const [isEditingGeo, setIsEditingGeo] = useState(false);
  const [isSavingGeo, setIsSavingGeo] = useState(false);
  const [jobTitle, setJobTitle] = useState('');
  const [bio, setBio] = useState('');
  const [linkedin, setLinkedin] = useState('');
  const [twitter, setTwitter] = useState('');

  // Timezone state
  const [timezone, setTimezone] = useState<string>('');
  const [isSavingTimezone, setIsSavingTimezone] = useState(false);

  // Preferences State
  const [isTranscriptionEnabled, setIsTranscriptionEnabled] = useState(false);
  const [isSavingPreferences, setIsSavingPreferences] = useState(false);

  // Image cropping state
  const [showCropModal, setShowCropModal] = useState(false);
  const [imgSrc, setImgSrc] = useState('');
  const imgRef = useRef<HTMLImageElement>(null);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const aspect = 1;

  // Initial root data fetch
  useEffect(() => {
    const fetchRootData = async () => {
      try {
        const [profileRes, settingsRes] = await Promise.all([
          apiClient.get('/api/user/profile'),
          apiClient.get('/api/user/settings').catch(() => ({ data: {} }))
        ]);

        const data = profileRes.data;
        setProfileData(data);
        setAvatarUrl(formatAvatarUrl(user?.photoURL || data.avatarUrl || null));

        const settings = settingsRes.data;
        setTimezone(settings.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone);
        setIsTranscriptionEnabled(settings.voiceTranscription || false);

        // Load Global GEO Authority fields
        setJobTitle(settings.jobTitle || '');
        setBio(settings.bio || '');
        setLinkedin(settings.socialLinks?.linkedin || '');
        setTwitter(settings.socialLinks?.twitter || '');
      } catch (error) {
        console.error('Error fetching root profile data:', error);
      }
    };

    if (user) {
      fetchRootData();
    }
  }, [user]);

  const handleSaveGeo = async () => {
    setIsSavingGeo(true);
    try {
      const settingsRes = await apiClient.get('/api/user/settings');
      const updatedSettings = {
        ...settingsRes.data,
        jobTitle,
        bio,
        socialLinks: { linkedin, twitter }
      };
      await apiClient.put('/api/user/settings', updatedSettings);
      toast.success('Author identity updated');
      setIsEditingGeo(false);
    } catch (error) {
      toast.error('Failed to update identity');
    } finally {
      setIsSavingGeo(false);
    }
  };

  const handleSaveTimezone = async (newTimezone: string) => {
    if (!user || isSavingTimezone || newTimezone === timezone) return;
    setIsSavingTimezone(true);
    try {
      const settingsResponse = await apiClient.get('/api/user/settings');
      const updatedSettings = { ...settingsResponse.data, timezone: newTimezone };
      await apiClient.put('/api/user/settings', updatedSettings);
      setTimezone(newTimezone);
      toast.success('Timezone updated');
    } catch (error) {
      toast.error('Failed to update timezone');
    } finally {
      setIsSavingTimezone(false);
    }
  };

  const handleToggleTranscription = async (enabled: boolean) => {
    if (!user || isSavingPreferences) return;
    setIsSavingPreferences(true);
    try {
      const settingsResponse = await apiClient.get('/api/user/settings');
      const updatedSettings = { ...settingsResponse.data, voiceTranscription: enabled };
      await apiClient.put('/api/user/settings', updatedSettings);
      setIsTranscriptionEnabled(enabled);
      toast.success(`Voice transcription ${enabled ? 'enabled' : 'disabled'}`);
      await refreshUserStatus();
    } catch (error) {
      toast.error('Failed to update preferences');
    } finally {
      setIsSavingPreferences(false);
    }
  };

  const handleSaveRootProfile = async () => {
    if (!user) return;
    setIsSavingProfile(true);
    try {
      await apiClient.put('/api/user/profile', { displayName });
      toast.success('Account profile updated');
      await refreshUserStatus();
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setIsSavingProfile(false);
      setIsEditingProfile(false);
    }
  };

  const handleCreateNewPersona = async () => {
    setIsManagerModalOpen(true);
  };

  const handlePhotoClick = () => fileInputRef.current?.click();

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      if (file.size > 2 * 1024 * 1024) {
        toast.error('File size exceeds 2MB limit');
        return;
      }
      setSelectedFile(file);
      setShowCropModal(true);
      const reader = new FileReader();
      reader.onload = () => setImgSrc(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    const newCrop = centerAspectCrop(width, height, aspect);
    setCrop(newCrop);
    setCompletedCrop({
      unit: 'px',
      x: (newCrop.x / 100) * width,
      y: (newCrop.y / 100) * height,
      width: (newCrop.width / 100) * width,
      height: (newCrop.height / 100) * height
    });
  }, [aspect]);

  const handleCropComplete = async () => {
    if (!completedCrop || !imgRef.current || !selectedFile || !user) return;
    setShowCropModal(false);
    setAvatarLoading(true);
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('No 2d context');
      const pixelRatio = window.devicePixelRatio;
      canvas.width = completedCrop.width * pixelRatio;
      canvas.height = completedCrop.height * pixelRatio;
      ctx.scale(pixelRatio, pixelRatio);
      ctx.imageSmoothingQuality = 'high';
      const image = imgRef.current;
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;
      ctx.drawImage(image, completedCrop.x * scaleX, completedCrop.y * scaleY, completedCrop.width * scaleX, completedCrop.height * scaleY, 0, 0, completedCrop.width, completedCrop.height);
      const blob = await new Promise<Blob>((resolve) => canvas.toBlob((b) => b && resolve(b), 'image/png'));
      const croppedFile = new File([blob], 'avatar.png', { type: 'image/png' });
      const formData = new FormData();
      formData.append('avatar', croppedFile);
      const response = await apiClient.post('/api/user/avatar', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setAvatarUrl(formatAvatarUrl(response.data.photoUrl));
      toast.success('Photo updated');
      await refreshUserStatus();
    } catch (error) {
      toast.error('Failed to upload photo');
    } finally {
      setAvatarLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex justify-between items-end">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Experience Management</h1>
            <p className="text-gray-600 mt-1">Manage all your account settings and writing personas in one place</p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex items-center justify-between mb-6 overflow-x-auto pb-2 border-b border-gray-200">
            <TabsList className="bg-transparent h-auto p-0 gap-8">
              <TabsTrigger
                value="account"
                className="data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 border-b-2 border-transparent px-1 py-4 bg-transparent shadow-none rounded-none text-gray-500 font-semibold transition-all"
              >
                Account Settings
              </TabsTrigger>
              {profiles.map(p => (
                <TabsTrigger
                  key={p.id}
                  value={p.id}
                  className="data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 border-b-2 border-transparent px-1 py-4 bg-transparent shadow-none rounded-none text-gray-500 font-semibold flex items-center gap-2 transition-all whitespace-nowrap"
                >
                  <span className="text-lg">{p.avatarEmoji}</span>
                  <span>{p.name}</span>
                  {p.isDefault && <CheckCircleIcon className="h-4 w-4 text-blue-500 flex-shrink-0" />}
                </TabsTrigger>
              ))}
            </TabsList>

            <button
              onClick={handleCreateNewPersona}
              className="flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-800 bg-white px-3 py-1.5 rounded-md border border-gray-200 shadow-sm transition-all active:scale-95 flex-shrink-0 ml-4 mb-2"
            >
              <PlusIcon className="h-4 w-4" />
              <span>Add Writing Profile</span>
            </button>
          </div>

          {/* GLOBAL ACCOUNT TAB */}
          <TabsContent value="account" className="space-y-8 animate-in fade-in duration-300 outline-none">
            <ProfileSection title="Personal Information" description="Your primary account contact and identity details.">
              <PersonalInfoSection
                user={user}
                profileData={profileData}
                displayName={displayName}
                setDisplayName={setDisplayName}
                isEditing={isEditingProfile}
                setIsEditing={setIsEditingProfile}
                isSaving={isSavingProfile}
                avatarLoading={avatarLoading}
                avatarUrl={avatarUrl}
                isUploading={false}
                fileInputRef={fileInputRef}
                handlePhotoClick={handlePhotoClick}
                handlePhotoChange={handlePhotoChange}
                handleSave={handleSaveRootProfile}
                handleCancel={() => { setDisplayName(user?.displayName || ''); setIsEditingProfile(false); }}
                handleKeyDown={(e) => e.key === 'Enter' && handleSaveRootProfile()}
              />
            </ProfileSection>

            <ProfileSection title="Author Identity (GEO)" description="Global expertise details and social links used across all AI writing.">
              <GeoAuthoritySection
                isEditing={isEditingGeo}
                setIsEditing={setIsEditingGeo}
                isSaving={isSavingGeo}
                handleSave={handleSaveGeo}
                handleCancel={() => { setIsEditingGeo(false); }}
                jobTitle={jobTitle} setJobTitle={setJobTitle}
                bio={bio} setBio={setBio}
                linkedinData={linkedin} setLinkedinData={setLinkedin}
                twitterData={twitter} setTwitterData={setTwitter}
              />
            </ProfileSection>

            <ProfileSection title="Organization Profile" description="Global organization settings, branding, and legal identity.">
              <div className="space-y-8">
                <OrganizationProfileSection />
                <div className="border-t border-gray-100 pt-8">
                  <BusinessLogoSection />
                </div>
              </div>
            </ProfileSection>

            <ProfileSection title="AI Shortcuts" description="Reusable AI prompts available across your entire account.">
              <AIShortcutsSection />
            </ProfileSection>

            <ProfileSection title="Localization & Preferences" description="Global application behavior and account-wide preferences.">
              <div className="space-y-8">
                <TimezoneSection
                  timezone={timezone}
                  isSavingTimezone={isSavingTimezone}
                  handleSaveTimezone={handleSaveTimezone}
                />
                <div className="border-t border-gray-100 pt-6">
                  <PreferencesSection
                    isTranscriptionEnabled={isTranscriptionEnabled}
                    onToggleTranscription={handleToggleTranscription}
                    isSaving={isSavingPreferences}
                  />
                </div>
              </div>
            </ProfileSection>
          </TabsContent>

          {/* PERSONA-SPECIFIC TABS */}
          {profiles.map(p => (
            <TabsContent key={p.id} value={p.id} className="space-y-8 animate-in fade-in duration-300 outline-none">
              <PersonaManagementTab
                profile={p}
                onDeleted={async () => {
                  await refreshProfiles();
                  setActiveTab('account');
                }}
              />
            </TabsContent>
          ))}
        </Tabs>

        <ImageCropModal
          showCropModal={showCropModal} imgSrc={imgSrc} imgRef={imgRef}
          crop={crop || { unit: '%', width: 90, height: 90, x: 5, y: 5 }}
          setCrop={setCrop} completedCrop={completedCrop || null} setCompletedCrop={setCompletedCrop}
          aspect={aspect} onImageLoad={onImageLoad} handleCropCancel={() => setShowCropModal(false)}
          handleCropComplete={handleCropComplete}
        />

        <ProfileManagerModal
          isOpen={isManagerModalOpen}
          onClose={() => setIsManagerModalOpen(false)}
          initialViewMode="create"
          onProfileSwitched={(p) => setActiveTab(p.id)}
        />
      </div>
    </div>
  );
}
